import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { 
  FunnelIcon, 
  XMarkIcon, 
  AdjustmentsHorizontalIcon 
} from '@heroicons/react/24/outline';

const TourFilters = ({ filters, onFilterChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  // Update local filters when parent filters change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Apply filters immediately when local filters change
  useEffect(() => {
    // Use a small delay to avoid too many API calls while typing
    const timeoutId = setTimeout(() => {
      onFilterChange(localFilters);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [localFilters, onFilterChange]);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  
  // Fetch categories from the API
  const { data: categoriesData } = useQuery('tourCategories', async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/v1/categories?type=tour`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  }, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
  
  // Use API categories or fallback to empty array
  const categories = categoriesData || [];

  const durations = [
    { value: '1-3', label: '1-3 days' },
    { value: '4-7', label: '4-7 days' },
    { value: '8-14', label: '8-14 days' },
    { value: '15+', label: '15+ days' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'popularity', label: 'Most Popular' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryChange = (categoryId) => {
    setLocalFilters(prev => {
      // Check if category ID is already in the array
      const categoryExists = prev.category.includes(categoryId.toString());
      
      // If it exists, remove it; otherwise, add it
      const updatedCategories = categoryExists
        ? prev.category.filter(cat => cat !== categoryId.toString())
        : [...prev.category, categoryId.toString()];
        
      return {
        ...prev,
        category: updatedCategories
      };
    });
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      category: [],
      minPrice: '',
      maxPrice: '',
      duration: '',
      search: '',
      sort: 'newest'
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  // Mobile filter toggle
  const toggleFilters = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Mobile filter button */}
      <div className="lg:hidden mb-6">
        <button
          onClick={toggleFilters}
          className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-3 text-gray-700 font-medium"
        >
          <FunnelIcon className="h-5 w-5" />
          {isOpen ? 'Hide Filters' : 'Show Filters'}
        </button>
      </div>

      {/* Filters sidebar - responsive */}
      <div className={`${isOpen ? 'block' : 'hidden'} lg:block bg-white rounded-lg shadow-md p-6 sticky top-24`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">Filters</h3>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleClearFilters}
              className="text-sm text-gray-500 hover:text-primary"
            >
              Clear All
            </button>
            <button 
              onClick={toggleFilters} 
              className="lg:hidden"
            >
              <XMarkIcon className="h-6 w-6 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
            Search
          </label>
          <input
            type="text"
            id="search"
            name="search"
            value={localFilters.search}
            onChange={handleInputChange}
            placeholder="Search tours..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Categories */}
        <div className="mb-6">
          <h4 className="font-medium mb-3">Categories</h4>
          <div className="space-y-2">
            {categories.map(category => (
              <div key={category.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`category-${category.id}`}
                  checked={localFilters.category.includes(category.id.toString())}
                  onChange={() => handleCategoryChange(category.id)}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label
                  htmlFor={`category-${category.id}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {category.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Price Range */}
        <div className="mb-6">
          <h4 className="font-medium mb-3">Price Range</h4>
          <div className="flex space-x-4">
            <div>
              <label htmlFor="minPrice" className="block text-sm text-gray-600 mb-1">
                Min ($)
              </label>
              <input
                type="number"
                id="minPrice"
                name="minPrice"
                value={localFilters.minPrice}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="maxPrice" className="block text-sm text-gray-600 mb-1">
                Max ($)
              </label>
              <input
                type="number"
                id="maxPrice"
                name="maxPrice"
                value={localFilters.maxPrice}
                onChange={handleInputChange}
                placeholder="Any"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Duration */}
        <div className="mb-6">
          <h4 className="font-medium mb-3">Duration</h4>
          <div className="space-y-2">
            {durations.map(({ value, label }) => (
              <div key={value} className="flex items-center">
                <input
                  type="radio"
                  id={`duration-${value}`}
                  name="duration"
                  value={value}
                  checked={localFilters.duration === value}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                />
                <label
                  htmlFor={`duration-${value}`}
                  className="ml-2 text-sm text-gray-700"
                >
                  {label}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Sort By */}
        <div className="mb-6">
          <label htmlFor="sort" className="block font-medium mb-2">
            Sort By
          </label>
          <select
            id="sort"
            name="sort"
            value={localFilters.sort}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Apply Filters Button (Mobile only) */}
        <div className="lg:hidden">
          <button
            onClick={handleApplyFilters}
            className="w-full bg-primary hover:bg-primary-dark text-white py-2 px-4 rounded-md transition-colors duration-300 flex items-center justify-center gap-2"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

export default TourFilters;
